import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Layers, ShoppingBag } from 'lucide-react';
import { Heading } from '../layout';
import { TabNavigation } from '../reusable';
import { useUIColors } from '../../hooks/useTheme';
import PurchaseRequestsTable from '../purchaseRequests/PurchaseRequestsTable';
import { PurchaseRequestsForm } from '../forms/PurchaseRequestsForm';
import PurchaseRequestDetailModal from './PurchaseRequestDetailModal';
import purchaseRequestsAPI from '../../services/purchaseRequestsAPI';
import suppliersAPI from '../../services/suppliersAPI';
import {
  PURCHASE_REQUEST_AREAS,
  normalizeDepartment,
  purchaseRequestAreaOptions,
} from './purchaseRequestAreas';
import {
  isCommercialUpsellingRequest,
  notifyPurchaseRequestsChanged,
  PURCHASE_REQUESTS_CHANGED_EVENT,
} from './commercialRequestsUtils';

const todayIso = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = {
  request_code: '',
  requester_name: '',
  department: '',
  supplier_id: '',
  request_date: todayIso(),
  needed_by_date: '',
  priority: 'Normal',
  status: 'Borrador',
  justification: '',
  notes: '',
  items: [{ id: null, item_code: '', description: '', quantity: 1, unit: 'UN', estimated_unit_price: 0 }],
};

const matchesArea = (row, areaName) =>
  normalizeDepartment(row?.department).toLowerCase() === normalizeDepartment(areaName).toLowerCase();

const PaintPurchaseRequestsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [suppliers, setSuppliers] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [detailRequest, setDetailRequest] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await purchaseRequestsAPI.getAll();
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudieron cargar las solicitudes.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLookups = useCallback(async () => {
    try {
      const suppliersData = await suppliersAPI.getAll();
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
    } catch (err) {
      console.error('Error loading request lookups:', err);
    }
  }, []);

  useEffect(() => {
    loadRequests();
    loadLookups();
  }, [loadRequests, loadLookups]);

  useEffect(() => {
    const refresh = () => {
      loadRequests();
    };
    window.addEventListener(PURCHASE_REQUESTS_CHANGED_EVENT, refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener(PURCHASE_REQUESTS_CHANGED_EVENT, refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [loadRequests]);

  const supplierOptions = useMemo(
    () =>
      suppliers.map((s) => ({
        value: String(s.id),
        label: `${s.supplier_code || ''} · ${s.name}`.trim(),
      })),
    [suppliers]
  );

  const areasInData = useMemo(() => {
    const seen = new Set();
    const list = [];
    requests.forEach((row) => {
      const name = normalizeDepartment(row.department);
      if (!name) return;
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      list.push(name);
    });
    return list.sort((a, b) => a.localeCompare(b, 'es'));
  }, [requests]);

  const areaTabs = useMemo(() => {
    const seen = new Set();
    const list = [];
    [...PURCHASE_REQUEST_AREAS, ...areasInData].forEach((name) => {
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      list.push(name);
    });
    return list;
  }, [areasInData]);

  const areaOptions = useMemo(
    () => purchaseRequestAreaOptions(areasInData),
    [areasInData]
  );

  const countsByArea = useMemo(() => {
    const map = new Map();
    requests.forEach((row) => {
      const name = normalizeDepartment(row.department) || 'Sin área';
      map.set(name, (map.get(name) || 0) + 1);
    });
    return map;
  }, [requests]);

  const upsellingRequests = useMemo(
    () => requests.filter(isCommercialUpsellingRequest),
    [requests]
  );

  const openCreate = (prefillDepartment = '') => {
    setIsEditing(false);
    setEditingId(null);
    setFormError('');
    setFormData({
      ...EMPTY_FORM,
      request_date: todayIso(),
      department: prefillDepartment || '',
      items: [{ ...EMPTY_FORM.items[0] }],
    });
    setShowForm(true);
  };

  const openDetail = async (row) => {
    setDetailLoading(true);
    try {
      const full = await purchaseRequestsAPI.getById(row.id);
      setDetailRequest(full);
    } catch (err) {
      window.alert(err?.message || 'No se pudo cargar la solicitud.');
    } finally {
      setDetailLoading(false);
    }
  };

  const openEdit = async (request) => {
    const source = request?.items ? request : await purchaseRequestsAPI.getById(request.id);
    setDetailRequest(null);
    setIsEditing(true);
    setEditingId(source.id);
    setFormError('');
    setFormData({
      request_code: source.request_code || '',
      requester_name: source.requester_name || '',
      department: source.department || '',
      supplier_id: source.supplier_id ? String(source.supplier_id) : '',
      request_date: source.request_date ? String(source.request_date).slice(0, 10) : todayIso(),
      needed_by_date: source.needed_by_date ? String(source.needed_by_date).slice(0, 10) : '',
      priority: source.priority || 'Normal',
      status: source.status || 'Borrador',
      justification: source.justification || '',
      notes: source.notes || '',
      items:
        Array.isArray(source.items) && source.items.length
          ? source.items.map((item) => ({
              id: item.id,
              item_code: item.item_code || '',
              description: item.description || '',
              quantity: item.quantity ?? 1,
              unit: item.unit || 'UN',
              estimated_unit_price: item.estimated_unit_price ?? 0,
            }))
          : [{ id: null, item_code: '', description: '', quantity: 1, unit: 'UN', estimated_unit_price: 0 }],
    });
    setShowForm(true);
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const requester = String(formData.requester_name || '').trim();
    if (!requester) {
      setFormError('El solicitante es obligatorio.');
      return;
    }
    const department = String(formData.department || '').trim();
    if (!department) {
      setFormError('El área / departamento es obligatorio.');
      return;
    }
    const items = (formData.items || [])
      .map((item) => ({
        id: item.id || null,
        item_code: String(item.item_code || '').trim(),
        description: String(item.description || '').trim(),
        quantity: Number(item.quantity || 0),
        unit: String(item.unit || 'UN').trim() || 'UN',
        estimated_unit_price: Number(item.estimated_unit_price || 0),
      }))
      .filter((item) => item.description || item.item_code);
    if (!items.length) {
      setFormError('Agrega al menos un ítem con descripción.');
      return;
    }

    const payload = {
      requester_name: requester,
      department,
      supplier_id: Number(formData.supplier_id || 0) || null,
      request_date: formData.request_date || todayIso(),
      needed_by_date: formData.needed_by_date || null,
      priority: String(formData.priority || 'Normal').trim() || 'Normal',
      status: String(formData.status || 'Borrador').trim() || 'Borrador',
      justification: String(formData.justification || '').trim(),
      notes: String(formData.notes || '').trim(),
      items,
    };

    setIsSaving(true);
    setFormError('');
    try {
      if (isEditing && editingId) {
        await purchaseRequestsAPI.update(editingId, payload);
      } else {
        await purchaseRequestsAPI.create(payload);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ ...EMPTY_FORM });
      await loadRequests();
      notifyPurchaseRequestsChanged();
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar la solicitud.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    if (!ids?.length) return false;
    const ok = window.confirm(
      ids.length === 1 ? '¿Eliminar esta solicitud?' : `¿Eliminar ${ids.length} solicitudes?`
    );
    if (!ok) return false;
    await purchaseRequestsAPI.bulkDelete(ids);
    await loadRequests();
    return true;
  };

  const renderTable = (rows, title, defaultDepartment = '') => (
    <PurchaseRequestsTable
      rows={rows}
      loading={loading || detailLoading}
      error={error}
      isDarkMode={isDarkMode}
      tableTitle={title}
      attached
      onAdd={() => openCreate(defaultDepartment)}
      onRowClick={openDetail}
      onBulkDelete={handleBulkDelete}
    />
  );

  const tabs = [
    {
      key: 'consolidado',
      label: 'Consolidado',
      icon: <Layers size={16} />,
      badge: requests.length || undefined,
      content: renderTable(requests, 'Consolidado de solicitudes'),
    },
    {
      key: 'upselling',
      label: 'Upselling',
      icon: <ShoppingBag size={16} />,
      badge: upsellingRequests.length || undefined,
      content: renderTable(
        upsellingRequests,
        'Upselling · desde cierre comercial',
        'Comercial'
      ),
    },
    ...areaTabs.map((area) => {
      const areaRows = requests.filter((row) => matchesArea(row, area));
      const count = countsByArea.get(area) || areaRows.length || 0;
      return {
        key: `area:${area.toLowerCase()}`,
        label: area,
        icon: <Building2 size={16} />,
        badge: count || undefined,
        content: renderTable(areaRows, `Solicitudes · ${area}`, area),
      };
    }),
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Solicitudes de compra
        </Heading>
        <p style={{ margin: 0, maxWidth: 720, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Reúne los requerimientos de las distintas áreas. Incluye las solicitudes de upselling generadas al
          cerrar cotizaciones como vendida.
        </p>
      </div>

      <TabNavigation
        tabs={tabs}
        defaultTab="consolidado"
        isDarkMode={isDarkMode}
        style={{
          marginBottom: 0,
          borderTop: `1px solid ${ui.border}`,
          borderLeft: `1px solid ${ui.border}`,
          borderRight: `1px solid ${ui.border}`,
        }}
        contentStyle={{ marginTop: 0 }}
      />

      <PurchaseRequestsForm
        isOpen={showForm}
        onClose={() => !isSaving && setShowForm(false)}
        onSave={handleSave}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        isEditing={isEditing}
        formData={formData}
        onChange={handleChange}
        formError={formError}
        supplierOptions={supplierOptions}
        areaOptions={areaOptions}
      />

      <PurchaseRequestDetailModal
        isOpen={Boolean(detailRequest)}
        onClose={() => setDetailRequest(null)}
        purchaseRequest={detailRequest}
        isDarkMode={isDarkMode}
        onEdit={openEdit}
      />
    </div>
  );
};

export default PaintPurchaseRequestsWorkspace;
